import { useCallback, useMemo, useState, useRef, type CSSProperties } from 'react';
import { useTheme, type ThemeTokens } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { useIsMobileViewport } from '../hooks/useIsMobileViewport';
import { createContentBlockStyles } from '../styles/contentBlocks';

export interface ContactSupportSubmission {
  name: string;
  email: string;
  subject: string;
  message: string;
  url: string;
  files: File[];
  courseId?: string;
  lessonId?: string;
  blockId?: string;
}

export interface ContactSupportFormProps {
  learnerName?: string;
  learnerEmail?: string;
  isPreview?: boolean;
  courseId?: string;
  lessonId?: string;
  blockId?: string;
  onSubmit?: (data: ContactSupportSubmission) => Promise<void> | void;
  onCancel?: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_FILES = 5;

export function ContactSupportForm({
  learnerName = '',
  learnerEmail = '',
  isPreview = false,
  courseId,
  lessonId,
  blockId,
  onSubmit,
  onCancel,
}: ContactSupportFormProps) {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const sharedStyles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const styles = useMemo(() => createStyles(tokens, isSmallMobile), [tokens, isSmallMobile]);

  const [step, setStep] = useState<'form' | 'confirmation'>('form');
  const [name, setName] = useState(learnerName);
  const [email, setEmail] = useState(learnerEmail);
  const [emailTouched, setEmailTouched] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);
  const [inputFocused, setInputFocused] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const emailValid = EMAIL_REGEX.test(email);
  // NA-242: focus borders use the fixed UI accent — never the account accent.
  const focusBorderColor = tokens.ui.fixedAccent;
  const defaultBorderColor = tokens.borders.interactive;
  const errorBorderColor = tokens.semantic.error;
  const showEmailError = emailTouched && email.length > 0 && !emailValid;
  const canSubmit = emailValid && subject.trim().length > 0 && description.trim().length > 0;

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;
    setFiles((prev) => {
      const remaining = MAX_FILES - prev.length;
      if (remaining <= 0) return prev;
      const existing = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const deduped = Array.from(selected).filter((f) => !existing.has(`${f.name}:${f.size}`));
      return [...prev, ...deduped.slice(0, remaining)];
    });
  }, []);

  const handleUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSend = useCallback(async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(false);
    try {
      await onSubmit?.({
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: description.trim(),
        url: window.location.href,
        files,
        courseId,
        lessonId,
        blockId,
      });
      setStep('confirmation');
    } catch (err) {
      console.error('[ContactSupport] submit failed', err);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, submitting, name, email, subject, description, files, courseId, lessonId, blockId, onSubmit]);

  const handleGotIt = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  if (step === 'confirmation') {
    return (
      <div style={styles.container} role="status" aria-live="polite">
        <p style={styles.confirmationBody}>
          {t('contact_support_form.confirmation_body')}
        </p>
        {isPreview && (
          <p style={styles.previewNotice}>{t('contact_support_form.preview_notice')}</p>
        )}
        <div style={styles.buttonRowCenter}>
          <button
            type="button"
            style={{
              ...sharedStyles.actionButton,
              ...(hoveredBtn === 'gotit' ? sharedStyles.actionButtonHover : {}),
            }}
            onMouseEnter={() => setHoveredBtn('gotit')}
            onMouseLeave={() => setHoveredBtn(null)}
            onClick={handleGotIt}
            data-testid="contact-support-gotit"
          >
            {t('contact_support_form.got_it')}
          </button>
        </div>
      </div>
    );
  }

  const remainingFiles = MAX_FILES - files.length;

  return (
    <div style={styles.container}>
      <p style={styles.requiredIndicator}>{t('contact_support_form.required_indicator')}</p>

      <div style={styles.fieldGroup}>
        <label style={styles.label} htmlFor="contact-support-name">{t('contact_support_form.label_name')} <span style={{ color: tokens.text.muted }}>{t('contact_support_form.name_optional')}</span></label>
        <input
          id="contact-support-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onFocus={() => setInputFocused('name')}
          onBlur={() => setInputFocused(null)}
          style={{
            ...styles.input,
            borderColor: inputFocused === 'name' ? focusBorderColor : defaultBorderColor,
          }}
          data-testid="contact-support-name"
        />
      </div>

      <div style={styles.fieldGroup}>
        <label style={styles.label} htmlFor="contact-support-email">{t('contact_support_form.label_email')}</label>
        <input
          id="contact-support-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onFocus={() => setInputFocused('email')}
          onBlur={() => { setInputFocused(null); setEmailTouched(true); }}
          aria-invalid={showEmailError}
          aria-describedby={showEmailError ? 'contact-support-email-error' : undefined}
          style={{
            ...styles.input,
            borderColor: showEmailError
              ? errorBorderColor
              : inputFocused === 'email'
                ? focusBorderColor
                : defaultBorderColor,
          }}
          data-testid="contact-support-email"
        />
        {showEmailError && (
          <span id="contact-support-email-error" style={styles.errorText}>{t('contact_support_form.email_error')}</span>
        )}
      </div>

      <div style={styles.fieldGroup}>
        <label style={styles.label} htmlFor="contact-support-subject">{t('contact_support_form.label_subject')}</label>
        <input
          id="contact-support-subject"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          onFocus={() => setInputFocused('subject')}
          onBlur={() => setInputFocused(null)}
          style={{
            ...styles.input,
            borderColor: inputFocused === 'subject' ? focusBorderColor : defaultBorderColor,
          }}
          data-testid="contact-support-subject"
        />
      </div>

      <div style={styles.fieldGroup}>
        <label style={styles.label} htmlFor="contact-support-description">{t('contact_support_form.label_description')}</label>
        <textarea
          id="contact-support-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onFocus={() => setInputFocused('description')}
          onBlur={() => setInputFocused(null)}
          style={{
            ...styles.textarea,
            borderColor: inputFocused === 'description' ? focusBorderColor : defaultBorderColor,
          }}
          rows={4}
          data-testid="contact-support-description"
        />
      </div>

      {/* File Upload */}
      <div style={styles.fieldGroup}>
        <label style={styles.label} htmlFor="contact-support-file-input">
          {t('contact_support_form.label_attachment')}
        </label>
        {files.length > 0 && (
          <ul style={styles.fileList}>
            {files.map((file, i) => (
              <li key={`${file.name}-${i}`} style={styles.fileItem}>
                <div style={styles.fileTextGroup}>
                  <span style={styles.fileName}>{file.name}</span>
                  <span style={styles.fileSize}>{formatFileSize(file.size)}</span>
                </div>
                <button
                  type="button"
                  style={styles.removeFileBtn}
                  onClick={() => handleRemoveFile(i)}
                  aria-label={t('contact_support_form.aria_remove_file', { filename: file.name })}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          style={{
            ...styles.uploadZone,
            ...(remainingFiles <= 0 ? styles.uploadZoneDisabled : {}),
          }}
          onClick={() => remainingFiles > 0 && handleUploadClick()}
          disabled={remainingFiles <= 0}
          data-testid="contact-support-add-file"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
          </svg>
          <span>{remainingFiles === 1 ? t('contact_support_form.add_files_singular', { count: remainingFiles }) : t('contact_support_form.add_files_plural', { count: remainingFiles })}</span>
        </button>
        <input
          ref={fileInputRef}
          id="contact-support-file-input"
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
          data-testid="contact-support-file-input"
        />
      </div>

      {submitError && (
        <span role="alert" style={styles.errorText} data-testid="contact-support-error">
          {t('contact_support_form.submit_error')}
        </span>
      )}

      <div style={styles.buttonRow}>
        <button
          type="button"
          style={{
            ...sharedStyles.secondaryActionButton,
            ...(hoveredBtn === 'cancel' ? sharedStyles.secondaryActionButtonHover : {}),
          }}
          onMouseEnter={() => setHoveredBtn('cancel')}
          onMouseLeave={() => setHoveredBtn(null)}
          onClick={onCancel}
          data-testid="contact-support-cancel"
        >
          {t('contact_support_form.cancel')}
        </button>
        <button
          type="button"
          style={{
            ...sharedStyles.actionButton,
            ...(!canSubmit || submitting ? sharedStyles.actionButtonDisabled : {}),
            ...(hoveredBtn === 'send' && canSubmit && !submitting ? sharedStyles.actionButtonHover : {}),
          }}
          disabled={!canSubmit || submitting}
          onMouseEnter={() => setHoveredBtn('send')}
          onMouseLeave={() => setHoveredBtn(null)}
          onClick={() => { void handleSend(); }}
          data-testid="contact-support-send"
        >
          {t('contact_support_form.send')}
        </button>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function createStyles(tokens: ThemeTokens, isSmallMobile: boolean): Record<string, CSSProperties> {
  return {
    container: {
      padding: isSmallMobile ? tokens.spacing.lgPlus : '30px',
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lgPlus,
      overflow: 'auto',
      flex: 1,
    },
    requiredIndicator: {
      margin: 0,
      fontSize: tokens.typography.sizes.base,
      color: tokens.text.muted,
    },
    confirmationBody: {
      margin: 0,
      fontSize: tokens.typography.sizes.ui,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.primary,
      lineHeight: tokens.typography.lineHeights.uiSoft,
    },
    previewNotice: {
      margin: 0,
      fontSize: tokens.typography.sizes.ui,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.semantic.error,
      lineHeight: tokens.typography.lineHeights.uiSoft,
    },
    buttonRowCenter: {
      display: 'flex',
      justifyContent: 'center',
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    label: {
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.primary,
      lineHeight: tokens.typography.lineHeights.tight,
    },
    input: {
      width: '100%',
      height: '44px',
      padding: `0px ${tokens.spacing.lg}`,
      fontSize: tokens.typography.sizes.md,
      fontWeight: tokens.typography.weights.regular,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
      backgroundColor: tokens.backgrounds.page,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: tokens.borders.interactive,
      borderRadius: tokens.radius.xs,
      outline: 'none',
      transition: `border-color ${tokens.ui.transitionDefault}`,
      boxSizing: 'border-box' as const,
    },
    textarea: {
      width: '100%',
      minHeight: '100px',
      padding: tokens.spacing.lg,
      fontSize: tokens.typography.sizes.md,
      fontWeight: tokens.typography.weights.regular,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
      backgroundColor: tokens.backgrounds.page,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: tokens.borders.interactive,
      borderRadius: tokens.radius.xs,
      resize: 'vertical' as const,
      outline: 'none',
      transition: `border-color ${tokens.ui.transitionDefault}`,
      boxSizing: 'border-box' as const,
    },
    errorText: {
      fontSize: tokens.typography.sizes.base,
      color: tokens.semantic.error,
      marginTop: `-${tokens.spacing.xs}`,
    },
    uploadZone: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tokens.spacing.sm,
      width: '100%',
      padding: `${tokens.spacing.lg} ${tokens.spacing.xl}`,
      fontSize: tokens.typography.sizes.ui,
      fontFamily: tokens.typography.fontFamily,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.primary,
      backgroundColor: 'transparent',
      borderWidth: '2px',
      borderStyle: 'dashed',
      borderColor: tokens.borders.interactive,
      borderRadius: tokens.radius.md,
      cursor: 'pointer',
      transition: `border-color ${tokens.ui.transitionDefault}, background ${tokens.ui.transitionDefault}, opacity ${tokens.ui.transitionDefault}`,
    },
    uploadZoneDisabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
    fileList: {
      listStyle: 'none',
      margin: 0,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
    },
    fileItem: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      padding: `${tokens.spacing.lg} ${tokens.spacing.xl}`,
      backgroundColor: tokens.backgrounds.page,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: tokens.borders.default,
      borderRadius: tokens.radius.md,
    },
    fileTextGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      overflow: 'hidden',
      flex: 1,
      minWidth: 0,
    },
    fileName: {
      fontSize: tokens.typography.sizes.ui,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.primary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    fileSize: {
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.muted,
    },
    removeFileBtn: {
      background: 'none',
      borderWidth: 0,
      borderStyle: 'none',
      color: tokens.text.muted,
      cursor: 'pointer',
      padding: tokens.spacing.sm,
      lineHeight: 1,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    buttonRow: {
      display: 'flex',
      justifyContent: 'space-between',
      paddingTop: tokens.spacing.md,
    },
  };
}
